"use client";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { SECTION_SIDE_PADDING_DESKTOP_MEDIA } from "@/lib/game-sections";
import {
  columnsAfterSidePadding,
  usedPinterestGridColumns,
} from "@/lib/showcase-grid/centered-columns";
import { useLayoutEffect, useRef } from "react";

/**
 * Keeps `--games-grid-used-columns` in sync. Sparse centering shrinks the grid to
 * the content span. Side padding removes columns (one per 0.5) and leaves that
 * space on the sides. Tile size still matches the full viewport grid.
 */
export function usePinterestGridCentering(
  contentColumnSpan: number,
  centerWhenSparse = true,
  sidePadding = 0
) {
  const ref = useRef<HTMLDivElement>(null);
  const sidePaddingActive = useMediaQuery(SECTION_SIDE_PADDING_DESKTOP_MEDIA);
  const appliedSidePadding = sidePaddingActive ? sidePadding : 0;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const apply = () => {
      if (!centerWhenSparse && appliedSidePadding <= 0) {
        el.style.removeProperty("--games-grid-used-columns");
        return;
      }

      const raw = getComputedStyle(el).getPropertyValue("--games-grid-columns").trim();
      const viewportColumns = Number.parseInt(raw, 10);
      if (!Number.isFinite(viewportColumns) || viewportColumns < 1) return;

      const available = columnsAfterSidePadding(viewportColumns, appliedSidePadding);
      const used = centerWhenSparse
        ? usedPinterestGridColumns(contentColumnSpan, available)
        : available;
      el.style.setProperty("--games-grid-used-columns", String(used));
    };

    apply();
    if (!centerWhenSparse && appliedSidePadding <= 0) return;

    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      el.style.removeProperty("--games-grid-used-columns");
    };
  }, [contentColumnSpan, centerWhenSparse, appliedSidePadding]);

  return ref;
}
